(() => {
  const root = document.querySelector('[data-hb-social-proof]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const mobileViewport = window.matchMedia('(max-width: 47.99rem)').matches;
  if (mobileViewport && root.dataset.mobileEnabled !== 'true') return;
  if (!mobileViewport && root.dataset.desktopEnabled !== 'true') return;

  let products = [];

  const link = root.querySelector('[data-hb-social-proof-link]');
  const image = root.querySelector('[data-hb-social-proof-image]');
  const message = root.querySelector('[data-hb-social-proof-message]');
  const eyebrow = root.querySelector('[data-hb-social-proof-eyebrow]');
  const close = root.querySelector('[data-hb-social-proof-close]');
  const feedUrl = root.dataset.feedUrl || '';
  const copy = root.dataset.copy || 'Someone from [city] just purchased [product]';
  const initialDelay = Number(root.dataset.initialDelay) || 12000;
  const visibleDuration = Number(root.dataset.visibleDuration) || 5000;
  const interval = Number(root.dataset.interval) || 60000;
  const maximum = Math.max(1, Number(root.dataset.maximum) || 4);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const dismissedKey = 'hb-social-proof-dismissed';
  let timer;
  let timerPurpose = '';
  let scheduledAt = 0;
  let remainingShowDelay = 0;
  let shown = 0;
  let productIndex = 0;
  let visible = false;
  let dismissed = false;

  try {
    dismissed = window.sessionStorage.getItem(dismissedKey) === 'true';
  } catch (_) {}
  if (dismissed) return;

  const emit = (action, product) => {
    document.dispatchEvent(new CustomEvent(`hb:social-proof:${action}`, {
      detail: { productHandle: product?.handle || '' },
    }));
  };

  const onScreen = (element) => {
    if (!element || element.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0
      && rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0
      && rect.left < window.innerWidth && rect.top < window.innerHeight;
  };

  const consentLayer = (element) => element.matches(
    '#cookie-banner.active, .shopify-pc__banner__dialog'
  ) || Boolean(element.closest('.shopify-pc__banner__dialog'));

  const obscured = () => [...document.querySelectorAll(
    '#cookie-banner.active, .shopify-pc__banner__dialog, dialog[open], [role="dialog"][aria-modal="true"]'
  )].some((element) => element !== root && !consentLayer(element) && onScreen(element));

  const avoidBottomOverlays = () => {
    let bottom = 20;

    // Shopify's draft-theme preview bar is rendered inside a full-screen iframe
    // and covers the bottom 65px of the storefront. Keep the notification
    // visible while the merchant is reviewing an unpublished theme.
    if (document.querySelector('#PBarNextFrame[src*="shopifycloud/preview-bar"]')) {
      bottom = 85;
    }

    const teaser = document.querySelector('.hb-newsletter-popup__teaser');
    if (root.classList.contains('hb-social-proof--bottom-left') && onScreen(teaser)) {
      const rect = teaser.getBoundingClientRect();
      bottom = Math.max(bottom, Math.ceil(window.innerHeight - rect.top + 12));
    }

    if (bottom > 20) {
      root.style.setProperty('--hb-social-proof-bottom', `${bottom}px`);
    } else {
      root.style.removeProperty('--hb-social-proof-bottom');
    }
  };

  const safeFeedItem = (item) => {
    if (!item || typeof item !== 'object') return null;

    const city = typeof item.city === 'string' ? item.city.trim() : '';
    const handle = typeof item.handle === 'string' ? item.handle.trim() : '';
    const title = typeof item.title === 'string' ? item.title.trim() : '';
    const itemUrl = typeof item.url === 'string' ? item.url : '';
    const itemImage = typeof item.image === 'string' ? item.image : '';

    try {
      const productUrl = new URL(itemUrl, window.location.origin);
      const imageUrl = new URL(itemImage);
      if (
        !city || !handle || !title ||
        productUrl.origin !== window.location.origin ||
        !productUrl.pathname.startsWith('/products/') ||
        imageUrl.protocol !== 'https:' || imageUrl.hostname !== 'cdn.shopify.com'
      ) return null;

      return {
        city,
        handle,
        title,
        url: `${productUrl.pathname}${productUrl.search}`,
        image: imageUrl.toString(),
      };
    } catch (_) {
      return null;
    }
  };

  const loadVerifiedPurchases = async () => {
    if (!feedUrl) return;
    products = [];

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await window.fetch(feedUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        mode: 'cors',
        signal: controller.signal,
      });
      if (!response.ok) return;

      const payload = await response.json();
      const verified = payload?.ok === true && Array.isArray(payload.items)
        ? payload.items.map(safeFeedItem).filter(Boolean)
        : [];
      if (verified.length) products = verified;
    } catch (_) {
      // Never imply a purchase or city when the verified feed is unavailable.
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const messageFor = (product) => {
    return copy
      .split('[city]').join(product.city || '')
      .split('[product]').join(product.title);
  };

  const renderMessage = (product) => {
    const cityToken = '[city]';
    const productToken = '[product]';
    const cityIndex = copy.indexOf(cityToken);
    const afterCity = cityIndex >= 0 ? copy.slice(cityIndex + cityToken.length) : '';
    const productOffset = afterCity.indexOf(productToken);

    if (cityIndex < 0 || productOffset < 0) {
      message.textContent = messageFor(product);
      return;
    }

    const productIndex = cityIndex + cityToken.length + productOffset;
    const city = document.createElement('strong');
    city.className = 'hb-social-proof__city';
    city.textContent = product.city || '';

    const action = document.createElement('em');
    action.className = 'hb-social-proof__action';
    action.textContent = copy.slice(cityIndex + cityToken.length, productIndex);

    const productName = document.createElement('strong');
    productName.className = 'hb-social-proof__product';
    productName.textContent = product.title;

    message.replaceChildren(
      document.createTextNode(copy.slice(0, cityIndex)),
      city,
      action,
      productName,
      document.createTextNode(copy.slice(productIndex + productToken.length)),
    );
  };

  const clearTimer = () => {
    window.clearTimeout(timer);
    timer = undefined;
    timerPurpose = '';
  };

  const scheduleShow = (delay) => {
    clearTimer();
    remainingShowDelay = Math.max(0, delay);
    if (dismissed || shown >= maximum || !products.length || document.hidden) return;

    scheduledAt = Date.now();
    timerPurpose = 'show';
    timer = window.setTimeout(() => {
      timer = undefined;
      timerPurpose = '';
      remainingShowDelay = 0;
      show();
    }, remainingShowDelay);
  };

  const pauseShowTimer = () => {
    if (timerPurpose !== 'show' || timer === undefined) return;
    remainingShowDelay = Math.max(0, remainingShowDelay - (Date.now() - scheduledAt));
    clearTimer();
  };

  const resumeShowTimer = () => {
    if (
      document.hidden || visible || dismissed || shown >= maximum ||
      !products.length || timer !== undefined
    ) return;
    scheduleShow(remainingShowDelay);
  };

  const scheduleAutoHide = (delay) => {
    clearTimer();
    timerPurpose = 'hide';

    const attempt = () => {
      if (!visible) return;
      const active = document.activeElement;
      if (active && typeof root.contains === 'function' && root.contains(active)) {
        timer = window.setTimeout(attempt, 250);
        return;
      }
      hide('timeout');
    };

    timer = window.setTimeout(attempt, delay);
  };

  const hide = (reason = 'timeout') => {
    if (!visible) return;
    visible = false;
    root.classList.remove('is-visible');
    root.setAttribute('aria-hidden', 'true');
    clearTimer();
    window.setTimeout(() => { root.hidden = true; }, reducedMotion ? 0 : 200);

    if (reason === 'dismiss') {
      dismissed = true;
      remainingShowDelay = 0;
      try { window.sessionStorage.setItem(dismissedKey, 'true'); } catch (_) {}
      emit('dismiss', products[(productIndex - 1 + products.length) % products.length]);
      return;
    }
    scheduleShow(interval);
  };

  function show() {
    if (dismissed || shown >= maximum || !products.length) return;
    if (document.hidden) {
      remainingShowDelay = Math.max(1000, remainingShowDelay);
      return;
    }
    if (obscured()) {
      scheduleShow(1000);
      return;
    }

    const product = products[productIndex % products.length];
    productIndex += 1;
    shown += 1;

    link.href = product.url;
    link.dataset.productHandle = product.handle;
    image.src = product.image;
    image.width = 80;
    image.height = 80;
    eyebrow.textContent = 'Recent purchase';
    renderMessage(product);

    avoidBottomOverlays();
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    window.requestAnimationFrame(() => root.classList.add('is-visible'));
    visible = true;
    emit('view', product);
    scheduleAutoHide(visibleDuration);
  }

  link.addEventListener('click', () => {
    const product = products.find((item) => item.handle === link.dataset.productHandle);
    emit('click', product);
  });
  close.addEventListener('click', () => hide('dismiss'));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      pauseShowTimer();
    } else {
      resumeShowTimer();
    }
  });
  window.addEventListener('pagehide', pauseShowTimer);
  window.addEventListener('pageshow', resumeShowTimer);

  loadVerifiedPurchases().finally(() => {
    if (!products.length) return;
    productIndex = Math.floor(Math.random() * products.length);
    scheduleShow(initialDelay);
  });
})();
